<script setup lang="ts">
import { Tooltip } from "floating-vue";
import { computed } from "vue";
import { Pet } from "../core/pet";
import {
    获取神耀能量已激活效果,
    神耀能量击败奖励,
} from "../core/shenyaoEnergy";

const props = defineProps<{
    pet: Pet;
    pos: "left" | "right";
}>();

const buff = computed(() => props.pet.getBuff("神耀能量"));
const stacks = computed(() => buff.value?.stacks ?? 0);
const activeEffects = computed(() =>
    获取神耀能量已激活效果(stacks.value),
);
const placement = computed(() =>
    props.pos === "right" ? "bottom-end" : "bottom-start",
);
</script>

<template>
    <Tooltip v-if="buff" :placement="placement">
        <button
            type="button"
            class="shen-yao-energy-btn"
            :aria-label="`神耀能量 ${stacks} 层`"
        >
            <span class="shen-yao-energy-icon" aria-hidden="true"></span>
            <span class="shen-yao-energy-stacks">{{ stacks }}</span>
        </button>
        <template #popper>
            <div class="shen-yao-energy-tip">
                <div class="shen-yao-energy-tip-title">
                    神耀能量 {{ stacks }}/{{ buff.maxStacks }} 层
                </div>
                <div
                    v-if="activeEffects.length === 0"
                    class="shen-yao-energy-tip-empty"
                >
                    尚未激活层数效果
                </div>
                <div v-else class="shen-yao-energy-tip-effects">
                    <div v-for="effect in activeEffects" :key="effect.threshold">
                        {{ effect.threshold }} 层：{{ effect.text }}
                    </div>
                </div>
                <div class="shen-yao-energy-tip-reward">
                    {{ 神耀能量击败奖励 }}
                </div>
            </div>
        </template>
    </Tooltip>
</template>

<style scoped>
.shen-yao-energy-btn {
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 calc(var(--ui-unit) * 32);
    width: calc(var(--ui-unit) * 32);
    height: calc(var(--ui-unit) * 32);
    padding: 0;
    border: calc(var(--ui-unit) * 1) solid rgba(51, 192, 237, 0.88);
    border-radius: calc(var(--ui-unit) * 6);
    background: rgba(0, 0, 0, 0.38);
    color: #8cdef5;
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.shen-yao-energy-btn:hover,
.shen-yao-energy-btn:focus-visible {
    border-color: #8cdef5;
    background: rgba(51, 192, 237, 0.14);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(51, 192, 237, 0.42);
}

.shen-yao-energy-btn:focus-visible {
    outline: calc(var(--ui-unit) * 2) solid rgba(255, 255, 255, 0.85);
    outline-offset: calc(var(--ui-unit) * 2);
}

/* 保留正式神耀能量图标的固定尺寸占位区域。 */
.shen-yao-energy-icon {
    display: block;
    width: calc(var(--ui-unit) * 20);
    height: calc(var(--ui-unit) * 20);
}

.shen-yao-energy-stacks {
    position: absolute;
    right: calc(var(--ui-unit) * 1);
    bottom: calc(var(--ui-unit) * 1);
    min-width: calc(var(--ui-unit) * 12);
    height: calc(var(--ui-unit) * 12);
    padding: 0 calc(var(--ui-unit) * 2);
    border-radius: calc(var(--ui-unit) * 4) 0 calc(var(--ui-unit) * 4) 0;
    background: rgba(51, 192, 237, 0.95);
    color: #0c2530;
    font-size: calc(var(--ui-unit) * 9);
    font-weight: 800;
    line-height: calc(var(--ui-unit) * 12);
    text-align: center;
    pointer-events: none;
}

.shen-yao-energy-tip {
    display: flex;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 5);
    max-width: calc(var(--ui-unit) * 360);
    line-height: 1.45;
}

.shen-yao-energy-tip-title {
    color: #8cdef5;
    font-weight: 800;
}

.shen-yao-energy-tip-effects {
    display: flex;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 2);
}

.shen-yao-energy-tip-empty {
    opacity: 0.8;
}

.shen-yao-energy-tip-reward {
    padding-top: calc(var(--ui-unit) * 4);
    border-top: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.14);
}
</style>
